import * as React from "react";
import { useCurrentPdf } from "@/modules/chat/hooks/useCurrentPdf";
import { copyArtefactContent } from "@/helpers/clipboard";
import { CopyActionButton } from "@/components/chat/actions/CopyActionButton";
import { useCopyAction } from "@/hooks/useCopyAction";
import { ARTEFACT_FRAME_URL } from "./frame";
import cssUrl from "@/index.css?url";

export const ArtefactsViewer: React.FC = () => {
  const { artefacts, selectedArtefactId } = useCurrentPdf();
  const { copied, executeCopy } = useCopyAction();
  const iframeRef = React.useRef<HTMLIFrameElement>(null);

  const artefact = artefacts.find(row => row.id === selectedArtefactId);
  const contentRef = React.useRef(artefact?.content ?? "");
  contentRef.current = artefact?.content ?? "";

  React.useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.source !== iframeRef.current?.contentWindow) return;
      if (e.data?.type === "artefact-ready") {
        iframeRef.current?.contentWindow?.postMessage(
          { type: "artefact-init", mode: "expanded", cssHref: cssUrl, content: contentRef.current },
          "*",
        );
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const artefactId = artefact?.id;
  const artefactContent = artefact?.content;
  React.useEffect(() => {
    if (artefactId === undefined || artefactContent === undefined) return;
    iframeRef.current?.contentWindow?.postMessage({ type: "artefact-update", content: artefactContent }, "*");
  }, [artefactId, artefactContent]);

  const handleCopy = () =>
    executeCopy(async () => {
      if (!artefact) return;
      await copyArtefactContent(artefact.content, iframeRef.current);
    });

  if (!artefact) return null;

  return (
    <div className="flex h-full flex-col">
      <div className="bg-ivory-mist-50 flex items-center justify-between border-b border-desert-sand-200 px-3 py-1.5">
        <span className="text-xs font-medium text-muted-foreground">{artefact.name}</span>
        <CopyActionButton copied={copied} onCopy={handleCopy} />
      </div>
      <iframe
        key={artefact.id}
        ref={iframeRef}
        src={ARTEFACT_FRAME_URL}
        style={{ width: "100%", height: "100%", border: "none", padding: 8 }}
        sandbox="allow-scripts"
      />
    </div>
  );
};
